/**
 * AttendanceTab — orchestrator for the daily attendance grid.
 *
 * Composition (Phase 1 refactor):
 *   attendance/useAttendanceData.js — status config, date window, grid load,
 *     optimistic local-update math
 *   attendance/AttendanceToolbar.jsx — filters, Mark Today, template/export/import
 *   attendance/AttendanceGrid.jsx — the table + all cell interactions
 *   attendance/RemarkPopover.jsx — Sheets-style cell comments
 * This file keeps only the mutation layer (mark/bulk/remark API calls with
 * optimistic rollback) and shared selection state.
 */
import { useState, useCallback, useMemo } from 'react';
import { MousePointer2, X } from 'lucide-react';
import { toast } from 'sonner';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { useTraineeFilter, ALL } from './TraineeFilterContext';
import { todayIso, cellKey } from './attendance/attendanceUtils';
import { useAttendanceData } from './attendance/useAttendanceData';
import { AttendanceToolbar } from './attendance/AttendanceToolbar';
import { AttendanceGrid } from './attendance/AttendanceGrid';
import { RemarkPopover } from './attendance/RemarkPopover';

export default function AttendanceTab() {
  const { post, put } = useApi();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('attendance.mark');
  const { batchId, scopeQS } = useTraineeFilter();
  const markBatch = batchId && batchId !== ALL ? batchId : undefined;

  const {
    activeStatuses, statusMap,
    startDate, endDate, setStartDate, setEndDate, shiftWindow, setWindowDays,
    data, setData, loading, load,
    applyLocalChanges, setCellRemark,
  } = useAttendanceData({ batchId, scopeQS });

  // ── Multi-select (shift-click) shared between grid + bulk action bar ──
  const [selection, setSelection] = useState(() => new Set());
  const clearSelection = useCallback(() => setSelection(new Set()), []);
  const isSelected = useCallback(
    (traineeId, date) => selection.has(cellKey(traineeId, date)),
    [selection],
  );
  const toggleSelection = useCallback((traineeId, date) => {
    const k = cellKey(traineeId, date);
    setSelection(prev => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  }, []);

  // ── Mutations — optimistic local update first, rollback on failure ──
  const markOne = async (traineeId, date, status) => {
    if (!canEdit) return;
    const snapshot = data;
    applyLocalChanges([{ trainee_id: traineeId, date, status }]);
    try {
      if (status === null) {
        await post('/trainees/attendance/unmark', { trainee_id: traineeId, date });
      } else {
        await post('/trainees/attendance/mark', { trainee_id: traineeId, date, status, batch_id: markBatch });
      }
    } catch (e) {
      setData(snapshot);
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const markColumn = async (date, status) => {
    if (!canEdit || data.rows.length === 0) return;
    const snapshot = data;
    const changes = data.rows.map(r => ({ trainee_id: r.trainee_id, date, status, batch_id: markBatch }));
    applyLocalChanges(changes);
    try {
      await post('/trainees/attendance/bulk', { marks: changes });
      toast.success(`Marked ${changes.length} trainees as ${statusMap[status]?.name || status}`);
    } catch (e) {
      setData(snapshot);
      toast.error(e.response?.data?.detail || 'Bulk mark failed');
    }
  };

  const markRow = async (traineeId, status) => {
    if (!canEdit || data.dates.length === 0) return;
    const snapshot = data;
    const changes = data.dates.map(d => ({ trainee_id: traineeId, date: d, status, batch_id: markBatch }));
    applyLocalChanges(changes);
    try {
      await post('/trainees/attendance/bulk', { marks: changes });
      const tName = data.rows.find(r => r.trainee_id === traineeId)?.name || 'trainee';
      toast.success(`Marked ${tName} as ${statusMap[status]?.name || status} for ${changes.length} days`);
    } catch (e) {
      setData(snapshot);
      toast.error(e.response?.data?.detail || 'Row mark failed');
    }
  };

  const bulkApply = useCallback(async (cellKeys, status) => {
    if (!canEdit || !cellKeys.size) return;
    const snapshot = data;
    const marks = Array.from(cellKeys).map(k => {
      const [trainee_id, date] = k.split('|');
      return { trainee_id, date, status, batch_id: markBatch };
    });
    applyLocalChanges(marks);
    clearSelection();
    try {
      await post('/trainees/attendance/bulk', { marks });
      toast.success(`Applied ${statusMap[status]?.name || status} to ${marks.length} cell${marks.length === 1 ? '' : 's'}`);
    } catch (e) {
      setData(snapshot);
      toast.error(e.response?.data?.detail || 'Bulk apply failed');
    }
  }, [canEdit, post, statusMap, clearSelection, applyLocalChanges, data, setData]);

  // Multi-cell clear (Ctrl+V paste of a copied empty mark).
  const clearCells = useCallback(async (cellKeys) => {
    if (!canEdit || !cellKeys.size) return;
    const snapshot = data;
    const cleared = Array.from(cellKeys).map(k => {
      const [trainee_id, date] = k.split('|');
      return { trainee_id, date, status: null };
    });
    applyLocalChanges(cleared);
    clearSelection();
    try {
      await Promise.all(cleared.map(c =>
        post('/trainees/attendance/unmark', { trainee_id: c.trainee_id, date: c.date })
      ));
    } catch {
      setData(snapshot);
      toast.error('Paste failed');
    }
  }, [canEdit, post, clearSelection, applyLocalChanges, data, setData]);

  const markAllToday = (status) => {
    const t = todayIso();
    if (t < startDate || t > endDate) {
      toast.error('Today is outside the visible date window. Click the column header instead.');
      return;
    }
    markColumn(t, status);
  };

  // ── Remarks (Sheets-style cell comments) ──
  const [remarkPopup, setRemarkPopup] = useState(null);
  const [remarkSaving, setRemarkSaving] = useState(false);

  const openRemarkEditor = (e, trainee, cell) => {
    e.preventDefault();
    e.stopPropagation();
    if (!cell.status) {
      if (canEdit) toast.info('Mark an attendance status first to add a comment');
      return;
    }
    const x = Math.min(e.clientX, window.innerWidth - 320);
    const y = Math.min(e.clientY, window.innerHeight - 260);
    setRemarkPopup({
      traineeId: trainee.trainee_id, traineeName: trainee.name,
      date: cell.date, text: cell.remark?.text || '', remark: cell.remark || null,
      x, y,
    });
  };

  const saveRemark = async (text) => {
    if (!remarkPopup) return;
    setRemarkSaving(true);
    try {
      const res = await put('/trainees/attendance/remark', {
        trainee_id: remarkPopup.traineeId, date: remarkPopup.date, text,
      });
      setCellRemark(remarkPopup.traineeId, remarkPopup.date, res.remark);
      toast.success(text.trim() ? 'Comment saved' : 'Comment deleted');
      setRemarkPopup(null);
    } catch (e2) {
      toast.error(e2.response?.data?.detail || 'Failed to save comment');
    } finally {
      setRemarkSaving(false);
    }
  };

  // First letter of short_code → status code (first definition wins).
  const hotkeyMap = useMemo(() => {
    const map = {};
    for (const s of activeStatuses) {
      const k = (s.short_code || s.name || '').toLowerCase().charAt(0);
      if (k && !map[k]) map[k] = s.code;
    }
    return map;
  }, [activeStatuses]);

  const summary = useMemo(() => {
    if (data.rows.length === 0) return null;
    const avg = data.rows.reduce((s, r) => s + r.attendance_pct, 0) / data.rows.length;
    const low = data.rows.filter(r => r.attendance_pct < 80 && r.total_working_days > 0).length;
    return { avg: avg.toFixed(1), low, total: data.rows.length };
  }, [data]);

  return (
    <div className="space-y-4" data-testid="attendance-tab">
      <AttendanceToolbar
        canEdit={canEdit}
        hasRows={data.rows.length > 0}
        activeStatuses={activeStatuses}
        startDate={startDate} endDate={endDate}
        setStartDate={setStartDate} setEndDate={setEndDate}
        setWindowDays={setWindowDays} shiftWindow={shiftWindow}
        markAllToday={markAllToday}
        scopeQS={scopeQS}
        onImported={load}
      />

      {canEdit && (
        <p className="text-xs text-muted-foreground flex items-center gap-1.5 flex-wrap" data-testid="attendance-hotkey-hint">
          <MousePointer2 className="w-3 h-3" />
          Tip: press
          {Object.keys(hotkeyMap).slice(0, 6).map(k => (
            <kbd key={k} className="px-1 bg-muted rounded text-[10px]">
              {k.toUpperCase()}
            </kbd>
          ))}
          to write a status ·
          <kbd className="px-1 bg-muted rounded text-[10px]">Shift</kbd>+click selects ·
          drag the corner dot to fill ·
          <kbd className="px-1 bg-muted rounded text-[10px]">Ctrl+C</kbd>/<kbd className="px-1 bg-muted rounded text-[10px]">V</kbd> copy-paste ·
          hover a row name for &ldquo;Fill row&rdquo;
        </p>
      )}

      {/* Bulk action bar — only when ≥1 cell selected via shift-click. */}
      {canEdit && selection.size > 0 && (
        <div className="flex items-center gap-3 flex-wrap p-2 rounded-md border bg-amber-50 dark:bg-amber-950/20"
             data-testid="bulk-action-bar">
          <span className="text-sm font-medium" data-testid="bulk-selection-count">
            {selection.size} cell{selection.size === 1 ? '' : 's'} selected
          </span>
          <span className="text-xs text-muted-foreground hidden sm:inline">
            Apply a status to all selected:
          </span>
          <div className="flex gap-1 flex-wrap">
            {activeStatuses.map(s => (
              <Button
                key={s.code}
                size="sm"
                variant="outline"
                onClick={() => bulkApply(selection, s.code)}
                title={`Apply ${s.name} to ${selection.size} cells`}
                data-testid={`bulk-apply-${s.code}`}
              >
                <span className={`inline-block w-4 h-4 rounded text-[9px] font-semibold flex items-center justify-center mr-1.5 ${s.color}`}>
                  {s.short_code}
                </span>
                {s.name}
              </Button>
            ))}
          </div>
          <Button size="sm" variant="ghost" onClick={clearSelection} className="ml-auto"
                  data-testid="bulk-clear-btn">
            <X className="w-3.5 h-3.5 mr-1" /> Clear
          </Button>
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap gap-2 text-xs">
        {activeStatuses.map(s => (
          <span key={s.code} className="inline-flex items-center gap-1">
            <span className={`inline-block w-5 h-5 rounded text-[10px] font-semibold flex items-center justify-center ${s.color}`}>
              {s.short_code}
            </span>
            {s.name}
          </span>
        ))}
      </div>

      {/* Summary */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Card className="p-3">
            <p className="text-xs text-muted-foreground">Trainees in view</p>
            <p className="font-display tabular-nums text-2xl font-bold">{summary.total}</p>
          </Card>
          <Card className="p-3">
            <p className="text-xs text-muted-foreground">Avg attendance</p>
            <p className="font-display tabular-nums text-2xl font-bold">{summary.avg}%</p>
          </Card>
          <Card className="p-3">
            <p className="text-xs text-muted-foreground">Below 80%</p>
            <p className="font-display tabular-nums text-2xl font-bold text-rose-600">{summary.low}</p>
          </Card>
        </div>
      )}

      <AttendanceGrid
        data={data} loading={loading} canEdit={canEdit}
        statusMap={statusMap} activeStatuses={activeStatuses} hotkeyMap={hotkeyMap}
        selection={selection} isSelected={isSelected}
        toggleSelection={toggleSelection} clearSelection={clearSelection}
        markOne={markOne} markColumn={markColumn} markRow={markRow}
        bulkApply={bulkApply} clearCells={clearCells}
        openRemarkEditor={openRemarkEditor}
      />

      <RemarkPopover
        popup={remarkPopup}
        canEdit={canEdit}
        saving={remarkSaving}
        onChangeText={(t) => setRemarkPopup(p => ({ ...p, text: t }))}
        onSave={saveRemark}
        onClose={() => setRemarkPopup(null)}
      />
    </div>
  );
}
